import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "../lib/api";
// OTP disabled — re-add when re-enabling: sendMobileOtp, verifyMobileOtp
// import { sendMobileOtp, verifyMobileOtp } from "../lib/api";
import { CS_SPECIALIZATIONS, LEAD_MODAL } from "../data/home-data";
import { LOGO_PNG } from "../data/site";

// ── Steps ────────────────────────────────────────────────────────────────────
const S = {
    MOBILE: 1,
    OTP: 2,
    ROLE: 3,
    // Student
    ST_NAME: 4,
    ST_EMAIL: 5, // "Are you looking to join the B.Tech program?" + email
    // University
    UN_NAME: 8,
    UN_CONTACT: 9,
    UN_DEPT: 10,
    UN_INTEREST: 11,
    UN_SPECIALIZATION: 12, // new step for specialization sub-selection
};

// Previous step for each step — powers the modal "back" button so users can
// return to an earlier question and change their answer.
const PREV_STEP = {
    [S.ROLE]: S.MOBILE,
    [S.ST_NAME]: S.ROLE,
    [S.ST_EMAIL]: S.ST_NAME,
    [S.UN_NAME]: S.ROLE,
    [S.UN_CONTACT]: S.UN_NAME,
    [S.UN_DEPT]: S.UN_CONTACT,
    [S.UN_INTEREST]: S.UN_DEPT,
    [S.UN_SPECIALIZATION]: S.UN_INTEREST,
};

const UNI_PROGRAMMES = LEAD_MODAL.uniProgrammes;

export default function LeadCaptureModal({ instantOpen = false }) {
    const [visible, setVisible] = useState(instantOpen);
    const [step, setStep] = useState(S.MOBILE);
    const [direction, setDirection] = useState(1);
    const [loading, setLoading] = useState(false);

    const [mobile, setMobile] = useState("");
    // OTP state (Plivo Verify) — disabled; kept for easy re-enable.
    // const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    // const [otpSending, setOtpSending] = useState(false);
    // const [timer, setTimer] = useState(0);
    // const sessionRef = useRef(null);
    // const otpRefs = useRef([]);

    // Role
    const [role, setRole] = useState(null);

    // Student fields
    const [sName, setSName] = useState("");
    const [sEmail, setSEmail] = useState("");

    // University fields
    const [uName, setUName] = useState("");
    const [uContact, setUContact] = useState("");
    const [uDesignation, setUDesignation] = useState("");
    const [uDept, setUDept] = useState("");
    const [uInterest, setUInterest] = useState(null);
    const [uSpecName, setUSpecName] = useState(null);
    const [uOther, setUOther] = useState("");

    // ── Show modal: instantly when triggered (e.g. banner "Apply Now"), ────────
    //    otherwise auto-open 1 min after every page visit (no persistence). ─────
    useEffect(() => {
        if (instantOpen) return; // already opened via trigger
        const t = setTimeout(() => setVisible(true), 60000);
        return () => clearTimeout(t);
    }, [instantOpen]);

    const dismiss = () => {
        setVisible(false);
    };

    const goTo = (s) => {
        setDirection(s > step ? 1 : -1);
        setStep(s);
    };

    // Go back to the previous question so the user can change an earlier answer.
    const goBack = () => {
        const prev = PREV_STEP[step];
        if (prev) goTo(prev);
    };

    // ── Timer helper (OTP resend countdown) — disabled with OTP ────────────────
    // const startTimer = () => {
    //     setTimer(30);
    //     const id = setInterval(() => {
    //         setTimer((p) => {
    //             if (p <= 1) { clearInterval(id); return 0; }
    //             return p - 1;
    //         });
    //     }, 1000);
    // };

    // ── Field validation helpers ──────────────────────────────────────────────
    // Indian mobile: 10 digits starting 6-9.
    const isValidMobile = (m) => /^[6-9]\d{9}$/.test(m);
    // Name: at least 2 chars, letters/spaces/.'- only (no digits/symbols).
    const isValidName = (n) => /^[A-Za-z][A-Za-z .'-]{1,}$/.test(n.trim());
    // Email: must contain an "@" with text on both sides and a domain dot.
    const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

    // ── OTP flow (DISABLED) ────────────────────────────────────────────────────
    // OTP verification via Plivo is temporarily turned off — we collect the
    // mobile number and remaining details without sending/verifying an OTP.
    // To re-enable: uncomment these, restore the S.MOBILE/S.OTP cases in
    // handleContinue, and re-add the OTP step UI block below.
    /*
    const sendOtp = async () => {
        setOtpSending(true);
        try {
            const { session_uuid } = await sendMobileOtp(`+91${mobile}`);
            sessionRef.current = session_uuid;
            startTimer();
            toast.success(`OTP sent to +91 ${mobile}`);
            goTo(S.OTP);
        } catch (err) {
            toast.error(err?.message || "Failed to send OTP");
        } finally {
            setOtpSending(false);
        }
    };

    const resendOtp = async () => {
        setOtp(["", "", "", "", "", ""]);
        sessionRef.current = null;
        await sendOtp();
    };

    const verifyOtp = async () => {
        const code = otp.join("");
        if (code.length < 6) return toast.error("Enter the 6-digit OTP");
        if (!sessionRef.current) return toast.error("Please resend OTP");
        setLoading(true);
        try {
            await verifyMobileOtp(sessionRef.current, code);
            toast.success("Mobile verified ✓");
            goTo(S.ROLE);
        } catch (err) {
            toast.error(err?.message || "Incorrect OTP. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (i, val) => {
        if (!/^\d*$/.test(val)) return;
        const next = [...otp];
        next[i] = val.slice(-1);
        setOtp(next);
        if (val && i < 5) otpRefs.current[i + 1]?.focus();
    };
    const handleOtpKey = (i, e) => {
        if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
    };
    */

    // ── Submit ────────────────────────────────────────────────────────────────
    const submitData = async (payload) => {
        setLoading(true);
        try {
            await submitLead(payload);
            toast.success(LEAD_MODAL.toasts.success);
            dismiss();
        } catch (err) {
            if (err?.alreadyRegistered) {
                toast.success(LEAD_MODAL.toasts.alreadyRegistered);
                dismiss();
            } else {
                toast.error(err?.message || LEAD_MODAL.toasts.errorGeneric);
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Role select — auto-advances ───────────────────────────────────────────
    const handleRoleSelect = (r) => {
        setRole(r);
        setTimeout(() => goTo(r === "student" ? S.ST_NAME : S.UN_NAME), 280);
    };

    // ── Primary CTA handler ───────────────────────────────────────────────────
    const handleContinue = async () => {
        switch (step) {
            case S.MOBILE:
                if (!isValidMobile(mobile))
                    return toast.error(LEAD_MODAL.toasts.invalidMobile);
                // OTP is disabled — skip verification and go straight to details.
                goTo(S.ROLE);
                break;
            // OTP step disabled:
            // case S.OTP:
            //     await verifyOtp();
            //     break;
            case S.ST_NAME:
                if (!isValidName(sName)) return toast.error(LEAD_MODAL.toasts.invalidName);
                goTo(S.ST_EMAIL); break;
            case S.ST_EMAIL:
                if (!isValidEmail(sEmail)) return toast.error(LEAD_MODAL.toasts.invalidEmail);
                await submitData({
                    mobile,
                    role: "student",
                    studentName: sName,
                    studentEmail: sEmail.trim(),
                });
                break;
            case S.UN_NAME:
                if (!uName.trim()) return toast.error(LEAD_MODAL.toasts.enterInstitution);
                goTo(S.UN_CONTACT); break;
            case S.UN_CONTACT:
                if (!isValidName(uContact)) return toast.error(LEAD_MODAL.toasts.invalidContactName);
                if (!uDesignation.trim()) return toast.error(LEAD_MODAL.toasts.enterDesignation);
                goTo(S.UN_DEPT); break;
            case S.UN_DEPT:
                if (!uDept.trim()) return toast.error(LEAD_MODAL.toasts.enterDept);
                goTo(S.UN_INTEREST); break;
            case S.UN_INTEREST:
                if (!uInterest) return toast.error(LEAD_MODAL.toasts.selectProgramme);
                if (uInterest === "other") {
                    if (!uOther.trim()) return toast.error(LEAD_MODAL.toasts.describeInterest);
                    await submitData({
                        mobile,
                        role: "university",
                        universityName: uName,
                        contactName: uContact,
                        contactPosition: uDesignation,
                        contactDepartment: uDept,
                        universityInterest: LEAD_MODAL.submitValues.universityOtherInterest,
                        universityOther: uOther,
                    });
                } else if (uInterest === "specialization") {
                    goTo(S.UN_SPECIALIZATION);
                } else {
                    await submitData({
                        mobile,
                        role: "university",
                        universityName: uName,
                        contactName: uContact,
                        contactPosition: uDesignation,
                        contactDepartment: uDept,
                        universityInterest: LEAD_MODAL.submitValues.universityPlacementInterest,
                        universityOther: "",
                    });
                }
                break;
            case S.UN_SPECIALIZATION:
                if (!uSpecName) return toast.error(LEAD_MODAL.toasts.selectSpecialization);
                await submitData({
                    mobile,
                    role: "university",
                    universityName: uName,
                    contactName: uContact,
                    contactPosition: uDesignation,
                    contactDepartment: uDept,
                    universityInterest: uSpecName,
                    universityOther: "",
                });
                break;
        }
    };

    // ── Progress dots ─────────────────────────────────────────────────────────
    const getProgress = () => {
        // Map step to a 1-based progress index out of 5 (OTP step disabled)
        const map = {
            [S.MOBILE]: 1, [S.ROLE]: 2,
            [S.ST_NAME]: 3, [S.ST_EMAIL]: 4,
            [S.UN_NAME]: 3, [S.UN_CONTACT]: 4, [S.UN_DEPT]: 5, [S.UN_INTEREST]: 5,
            [S.UN_SPECIALIZATION]: 5,
        };
        return map[step] || 1;
    };

    const slideVariants = {
        enter: (dir) => ({ x: dir > 0 ? 200 : -200, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (dir) => ({ x: dir > 0 ? -200 : 200, opacity: 0 }),
    };

    if (!visible) return null;

    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 30 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden relative"
                >
                    {/* Close button — only on first step */}
                    {step === S.MOBILE && (
                        <button
                            onClick={dismiss}
                            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-200 transition z-10 text-sm"
                        >
                            ✕
                        </button>
                    )}

                    {/* Back button — lets the user return to the previous question */}
                    {PREV_STEP[step] && (
                        <button
                            onClick={goBack}
                            aria-label="Go back"
                            className="absolute top-4 left-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition z-10"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                    )}

                    {/* Logo */}
                    <div className="flex justify-center pt-7 pb-1">
                        <img src={LOGO_PNG} alt={LEAD_MODAL.logoAlt} className="h-9 w-auto" />
                    </div>

                    {/* Sliding content */}
                    <div className="relative overflow-hidden" style={{ minHeight: 300 }}>
                        <AnimatePresence mode="wait" custom={direction}>
                            <motion.div
                                key={step}
                                custom={direction}
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                                className="px-8 py-5 flex flex-col items-center justify-center text-center min-h-[300px]"
                            >

                                {/* ── STEP 1: Mobile ── */}
                                {step === S.MOBILE && (
                                    <div className="flex flex-col items-center justify-center flex-1 w-full">
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-1">
                                            {LEAD_MODAL.mobileStep.headingPrefix}<span className="text-aiblue">{LEAD_MODAL.mobileStep.headingBrand}</span>
                                        </h2>
                                        <p className="text-sm text-gray-500 mb-6">{LEAD_MODAL.mobileStep.subheading}</p>
                                        <div className="w-full flex rounded-2xl border-2 border-gray-200 focus-within:border-aiblue transition overflow-hidden">
                                            <span className="flex items-center gap-1.5 px-3 sm:px-4 text-sm font-semibold text-gray-600 bg-gray-50 border-r border-gray-200 shrink-0">
                                                {/* India flag (inline SVG — renders reliably on all devices) */}
                                                <svg viewBox="0 0 9 6" className="h-3.5 w-5 rounded-[2px] shrink-0 ring-1 ring-gray-200" aria-hidden="true">
                                                    <rect width="9" height="6" fill="#fff" />
                                                    <rect width="9" height="2" fill="#FF9933" />
                                                    <rect y="4" width="9" height="2" fill="#138808" />
                                                    <circle cx="4.5" cy="3" r="0.7" fill="none" stroke="#000080" strokeWidth="0.18" />
                                                </svg>
                                                {LEAD_MODAL.mobileStep.countryCode}
                                            </span>
                                            <input
                                                type="tel"
                                                maxLength={10}
                                                value={mobile}
                                                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                                                placeholder={LEAD_MODAL.mobileStep.placeholder}
                                                autoFocus
                                                className="flex-1 min-w-0 h-13 px-3 sm:px-4 py-3.5 text-base font-medium text-center tracking-widest focus:outline-none bg-white"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* ── STEP 2: OTP (DISABLED — verification turned off) ──
                                {step === S.OTP && (
                                    <div className="flex flex-col items-center justify-center flex-1 w-full">
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-1">Verify OTP</h2>
                                        <p className="text-sm text-gray-500 mb-1">
                                            Sent to <span className="font-semibold text-gray-700">+91 {mobile}</span>
                                        </p>
                                        <button onClick={() => goTo(S.MOBILE)} className="text-xs text-aiblue hover:underline mb-5">
                                            Change number
                                        </button>
                                        <div className="flex justify-center gap-2 mb-3">
                                            {otp.map((d, i) => (
                                                <input
                                                    key={i}
                                                    ref={(el) => (otpRefs.current[i] = el)}
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={1}
                                                    value={d}
                                                    onChange={(e) => handleOtpChange(i, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKey(i, e)}
                                                    autoFocus={i === 0}
                                                    className="w-11 h-12 rounded-xl border-2 border-gray-200 text-center text-lg font-bold focus:border-aiblue focus:outline-none transition"
                                                />
                                            ))}
                                        </div>
                                        {timer > 0 ? (
                                            <p className="text-xs text-gray-400">Resend in {timer}s</p>
                                        ) : (
                                            <button onClick={resendOtp} className="text-xs text-aiblue font-medium hover:underline">
                                                Resend OTP
                                            </button>
                                        )}
                                    </div>
                                )}
                                ── end OTP step ── */}

                                {/* ── STEP 3: Role ── */}
                                {step === S.ROLE && (
                                    <>
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-5">{LEAD_MODAL.roleStep.heading}</h2>
                                        <div className="w-full space-y-3">
                                            {LEAD_MODAL.roles.map((r) => (
                                                <button
                                                    key={r.key}
                                                    onClick={() => handleRoleSelect(r.key)}
                                                    className={`w-full p-4 rounded-2xl border-2 text-left flex items-center gap-3 transition ${role === r.key ? "border-aiblue bg-aiblue/5 shadow-md" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}
                                                >
                                                    <span className="text-2xl">{r.icon}</span>
                                                    <div>
                                                        <div className="font-semibold text-gray-900 text-sm">{r.label}</div>
                                                        <div className="text-xs text-gray-500">{r.desc}</div>
                                                    </div>
                                                    {role === r.key && <CheckCircle2 className="ml-auto h-5 w-5 text-aiblue shrink-0" />}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {/* ── STUDENT: Name ── */}
                                {step === S.ST_NAME && (
                                    <div className="flex flex-col items-center justify-center flex-1 w-full">
                                        <p className="text-xs font-bold uppercase tracking-widest text-aiblue mb-1">{LEAD_MODAL.student.eyebrows.name}</p>
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-5">{LEAD_MODAL.student.headings.name}</h2>
                                        <input
                                            type="text"
                                            value={sName}
                                            onChange={(e) => setSName(e.target.value)}
                                            placeholder={LEAD_MODAL.student.placeholders.name}
                                            autoFocus
                                            className="w-full h-13 px-4 py-3.5 rounded-2xl border-2 border-gray-200 text-base font-medium text-center focus:border-aiblue focus:outline-none transition"
                                        />
                                    </div>
                                )}

                                {/* ── STUDENT: B.Tech program + email ── */}
                                {step === S.ST_EMAIL && (
                                    <div className="flex flex-col items-center justify-center flex-1 w-full">
                                        <p className="text-xs font-bold uppercase tracking-widest text-aiblue mb-1">{LEAD_MODAL.student.eyebrows.email}</p>
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-5">{LEAD_MODAL.student.headings.btech}</h2>
                                        <input
                                            type="email"
                                            value={sEmail}
                                            onChange={(e) => setSEmail(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && handleContinue()}
                                            placeholder={LEAD_MODAL.student.placeholders.email}
                                            autoFocus
                                            className="w-full h-13 px-4 py-3.5 rounded-2xl border-2 border-gray-200 text-base font-medium text-center focus:border-aiblue focus:outline-none transition"
                                        />
                                    </div>
                                )}

                                {/* ── UNIVERSITY: Name ── */}
                                {step === S.UN_NAME && (
                                    <div className="flex flex-col items-center justify-center flex-1 w-full">
                                        <p className="text-xs font-bold uppercase tracking-widest text-aiblue mb-1">{LEAD_MODAL.university.eyebrows.name}</p>
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-5">{LEAD_MODAL.university.headings.name}</h2>
                                        <input
                                            type="text"
                                            value={uName}
                                            onChange={(e) => setUName(e.target.value)}
                                            placeholder={LEAD_MODAL.university.placeholders.name}
                                            autoFocus
                                            className="w-full h-13 px-4 py-3.5 rounded-2xl border-2 border-gray-200 text-base font-medium text-center focus:border-aiblue focus:outline-none transition"
                                        />
                                    </div>
                                )}

                                {/* ── UNIVERSITY: Contact ── */}
                                {step === S.UN_CONTACT && (
                                    <div className="flex flex-col items-center justify-center flex-1 w-full">
                                        <p className="text-xs font-bold uppercase tracking-widest text-aiblue mb-1">{LEAD_MODAL.university.eyebrows.contact}</p>
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-4">{LEAD_MODAL.university.headings.contact}</h2>
                                        <div className="w-full space-y-3">
                                            <input
                                                type="text"
                                                value={uContact}
                                                onChange={(e) => setUContact(e.target.value)}
                                                placeholder={LEAD_MODAL.university.placeholders.contactName}
                                                autoFocus
                                                className="w-full h-13 px-4 py-3.5 rounded-2xl border-2 border-gray-200 text-base font-medium text-center focus:border-aiblue focus:outline-none transition"
                                            />
                                            <input
                                                type="text"
                                                value={uDesignation}
                                                onChange={(e) => setUDesignation(e.target.value)}
                                                placeholder={LEAD_MODAL.university.placeholders.designation}
                                                className="w-full h-13 px-4 py-3.5 rounded-2xl border-2 border-gray-200 text-base font-medium text-center focus:border-aiblue focus:outline-none transition"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* ── UNIVERSITY: Department ── */}
                                {step === S.UN_DEPT && (
                                    <div className="flex flex-col items-center justify-center flex-1 w-full">
                                        <p className="text-xs font-bold uppercase tracking-widest text-aiblue mb-1">{LEAD_MODAL.university.eyebrows.dept}</p>
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-5">{LEAD_MODAL.university.headings.dept}</h2>
                                        <input
                                            type="text"
                                            value={uDept}
                                            onChange={(e) => setUDept(e.target.value)}
                                            placeholder={LEAD_MODAL.university.placeholders.dept}
                                            autoFocus
                                            className="w-full h-13 px-4 py-3.5 rounded-2xl border-2 border-gray-200 text-base font-medium text-center focus:border-aiblue focus:outline-none transition"
                                        />
                                    </div>
                                )}

                                {/* ── UNIVERSITY: Interest ── */}
                                {step === S.UN_INTEREST && (
                                    <>
                                        <p className="text-xs font-bold uppercase tracking-widest text-aiblue mb-1">{LEAD_MODAL.university.eyebrows.interest}</p>
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-4">{LEAD_MODAL.university.headings.interest}</h2>
                                        <div className="w-full space-y-2.5">
                                            {UNI_PROGRAMMES.map((prog) => (
                                                <button
                                                    key={prog.key}
                                                    onClick={() => setUInterest(prog.key)}
                                                    className={`w-full p-3.5 rounded-xl border-2 text-left flex items-center gap-3 transition ${uInterest === prog.key ? "border-aiblue bg-aiblue/5" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}
                                                >
                                                    <span className={`text-sm font-semibold ${uInterest === prog.key ? "text-aiblue" : "text-gray-700"}`}>{prog.label}</span>
                                                    {uInterest === prog.key && <CheckCircle2 className="ml-auto h-4 w-4 text-aiblue shrink-0" />}
                                                </button>
                                            ))}
                                        </div>
                                        {/* Textarea for "Other" */}
                                        <AnimatePresence>
                                            {uInterest === "other" && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden w-full mt-3"
                                                >
                                                    <textarea
                                                        value={uOther}
                                                        onChange={(e) => setUOther(e.target.value)}
                                                        placeholder={LEAD_MODAL.university.placeholders.other}
                                                        autoFocus
                                                        className="w-full p-3 rounded-xl border-2 border-gray-200 text-sm focus:border-aiblue focus:outline-none transition min-h-[80px] resize-none text-center"
                                                    />
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </>
                                )}

                                {/* ── UNIVERSITY: Specialization Selection ── */}
                                {step === S.UN_SPECIALIZATION && (
                                    <>
                                        <p className="text-xs font-bold uppercase tracking-widest text-aiblue mb-1">{LEAD_MODAL.university.eyebrows.specialization}</p>
                                        <h2 className="font-display font-black text-xl text-gray-900 mb-4">{LEAD_MODAL.university.headings.specialization}</h2>
                                        <div className="w-full space-y-2">
                                            {CS_SPECIALIZATIONS.map((spec) => (
                                                <button
                                                    key={spec.name}
                                                    onClick={() => setUSpecName(spec.name)}
                                                    className={`w-full px-4 py-2.5 rounded-xl border-2 text-left text-sm font-medium transition flex items-center justify-between ${uSpecName === spec.name ? "border-aiblue bg-aiblue/5 text-aiblue" : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"}`}
                                                >
                                                    <span>{spec.name}</span>
                                                    {uSpecName === spec.name && <CheckCircle2 className="h-4 w-4 text-aiblue shrink-0" />}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}

                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* ── Bottom CTA (hidden on Role step — it auto-advances) ── */}
                    {step !== S.ROLE && (
                        <div className="px-8 pb-8">
                            <button
                                onClick={handleContinue}
                                disabled={loading}
                                className="w-full py-3.5 rounded-2xl bg-aiblue hover:bg-blue-700 disabled:opacity-50 text-white font-semibold text-sm flex items-center justify-center gap-2 transition"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                        {LEAD_MODAL.buttons.loading}
                                    </span>
                                ) : step === S.ST_EMAIL || step === S.UN_INTEREST || step === S.UN_SPECIALIZATION ? (
                                    <>{LEAD_MODAL.buttons.submit} <ArrowRight className="h-4 w-4" /></>
                                ) : (
                                    <>{LEAD_MODAL.buttons.continue} <ArrowRight className="h-4 w-4" /></>
                                )}
                            </button>

                            {/* Progress dots */}
                            <div className="flex justify-center gap-1.5 mt-4">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className={`rounded-full transition-all duration-300 ${i < getProgress() ? "w-4 h-1.5 bg-aiblue" : "w-1.5 h-1.5 bg-gray-200"}`}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </>
    );
}